import { useState, useEffect, useMemo } from 'react';
import { AspectRatio, Image, Box, Stack, VStack, Text, useDisclosure } from '@chakra-ui/react';

import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';

import BackwardBar from '~/components/BackwardBar';
import LocationBar from '~/components/LocationBar';
import FooterLoginSection from '~/components/FooterLoginSection';
import StoreLastPreOrderTimeNotice from '~/components/StoreLastPreOrderTimeNotice';

import {
  SingleModifierGroup,
  MultiModifierGroup,
  NoteInputSection,
  ItemInfoSection,
  ItemControlBar,
  StoreInfoBar,
  InventoryErrorModal,
  NotOperatingNotice,
} from './components';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import cartItemCountSelector from '~/recoil/selectors/cartItemCount';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import storeInfoAtom from '~/recoil/atom/storeInfo';
import platformAtom from '~/recoil/atom/platform';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue } from 'recoil';

import useStore from '~/hooks/useStore';
import useCartOrders from '~/hooks/useCartOrders';
import useItem from '~/pages/Item/hooks/useItem';
import useCartItem from '~/pages/Item/hooks/useCartItem';
import useScroll from '~/hooks/useScroll';
import useCartId from '~/hooks/useCartId';

import * as fp from 'lodash/fp';

import { v4 as uuidV4 } from 'uuid';

import getDateInventory from '~/utils/getDateInventory';
import isInOperatingTime from '~/utils/isInOperatingTime';
import isNotInHoliday from '~/utils/isNotInHoliday';

import { PLATFORM_ID } from '~/globalVariables';
import { CartItem, CartType } from '~/types/cart';

import errorImg from '~/assets/images/image_error.png';
import checkValidPreOrderTime from '~/utils/checkValidPreOrderTime';
import { PreorderTimeBar } from '../Home/components';
import transformDateRangeToString from '~/utils/transformDateRangeToString';
import globalEnterpriseGroupBuyAtom from '~/recoil/atom/enterpriseGroupBuy/globalEnterpriseGroupBuyAtom';
import getPath from '~/utils/getNavigatePath';

export default function AddItem() {
  const location = useLocation();
  const { storeId = '', itemId = '', enterpriseGroupBuyId = '', groupId = '' } = useParams();
  const auth = useRecoilValue(authAtom);
  const storeInfo = useRecoilValue(storeInfoAtom);
  const platform = useRecoilValue(platformAtom);
  const { data } = useRecoilValue(globalEnterpriseGroupBuyAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const itemCountInCart = useRecoilValue(cartItemCountSelector({ storeId, itemId }));
  const { positionInfo } = useScroll();

  const { cartType } = useCartId();

  useStore(storeId);
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);
  const [errorModifierId, setErrorModifierId] = useState<string>('');

  const { isOpen, onClose, onOpen } = useDisclosure();

  const itemWithModifiers = useItem(storeId, itemId);

  const { handleAddItem } = useCartOrders();

  const {
    count,
    note,
    cartModifiersMap,
    optionsTotalPrice,
    modifiersErrors,
    handleChangeCartModifiers,
    handleChangeCount,
    handleChangeNote,
  } = useCartItem(itemWithModifiers.modifiers);

  const handleAddToCartClick = async () => {
    if (fp.gt(modifiersErrors.size, 0)) {
      setShowError(true);
      setErrorModifierId(
        itemWithModifiers.modifiers.find(({ id }) => modifiersErrors.has(id))?.id || '',
      );
      return;
    }

    const hasOptionInventoryError = itemWithModifiers.modifiers.reduce<boolean>((acc, modifier) => {
      if (acc) {
        return acc;
      }

      const cartModifierOptionsSet = new Set(
        (cartModifiersMap.get(modifier.id)?.options || []).map(({ id }) => id),
      );

      return modifier.options.some(({ id, restInventory }) =>
        cartModifierOptionsSet.has(id) ? fp.lt(restInventory - count, 0) : false,
      );
    }, false);

    if (hasOptionInventoryError) {
      onOpen();
      return;
    }

    if (fp.lt(count, 1)) {
      return;
    }

    const cartItem: CartItem = {
      keyId: uuidV4(),
      id: itemWithModifiers.id,
      name: itemWithModifiers.name,
      price: Number(itemWithModifiers.price),
      tax: itemWithModifiers.tax,
      count: count,
      note: note,
      modifiers: [...cartModifiersMap.values()],
      containers: itemWithModifiers.containers[platform.id] || [],
    };
    try {
      await handleAddItem(storeId || '', storeInfo.name, cartItem);
      // @ts-ignore
      if (location?.state?.previous) {
        // @ts-ignore
        navigate(location.state.previous);
      } else {
        navigate(getPath('/', groupId, enterpriseGroupBuyId));
      }
    } catch (err) {
      console.error('add item error');
      console.log(err);
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  const isInStoreOperatingTime = useMemo(
    () =>
      isInOperatingTime(storeInfo.preOrderSetting.schedule, pickMealAt) &&
      isNotInHoliday(storeInfo.officialHoliday, pickMealAt),
    [pickMealAt, storeInfo.preOrderSetting.schedule, storeInfo.officialHoliday],
  );

  // 檢查最後結單時間是否超過現在時間
  const [isValidPreOrderTime, lastPreOrderTime] = useMemo(() => {
    const { schedule, beforeHours, afterHours } = storeInfo.preOrderSetting;
    const now = new Date();

    return checkValidPreOrderTime(schedule, afterHours, beforeHours, pickMealAt, now);
  }, [pickMealAt, storeInfo.preOrderSetting]);

  const showModifierErrors = showError ? modifiersErrors : new Set<string>();

  const itemTotal = (Number(itemWithModifiers.price) + Number(optionsTotalPrice)) * count;

  // 剩餘庫存計算
  const restOfItemInventory = (() => {
    const itemDateInventory = getDateInventory(itemWithModifiers.inventory, pickMealAt);
    return itemDateInventory - itemCountInCart;
  })();

  const isPlatformContainStore =
    currentPickMealPoint.stores.includes(storeInfo.id) &&
    platform.displayStores.includes(storeInfo.id);

  const isCountExceedInventory = count > restOfItemInventory;

  const isDisabled = useMemo(() => {
    if (!!enterpriseGroupBuyId) return !!isCountExceedInventory;
    return (
      !isPlatformContainStore ||
      isCountExceedInventory ||
      !isValidPreOrderTime ||
      !isInStoreOperatingTime
    );
  }, [
    enterpriseGroupBuyId,
    isPlatformContainStore,
    isCountExceedInventory,
    isValidPreOrderTime,
    isInStoreOperatingTime,
  ]);

  return (
    <PageContainer
      hideHeader
      hideFooter
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => {
              // @ts-ignore
              if (location?.state?.previous) {
                // @ts-ignore
                navigate(location.state.previous);
              } else {
                navigate(getPath('/', groupId, enterpriseGroupBuyId));
              }
            }}
          />
          <LocationBar />
          {!!enterpriseGroupBuyId && data?.pickMealAt && data?.pickMealEnd ? (
            <PreorderTimeBar
              value={transformDateRangeToString(
                data.pickMealAt.toDate(),
                data.pickMealEnd.toDate(),
              )}
              containerProps={{ bg: '#FFF', borderBottom: '1px solid #E5B369' }}
            />
          ) : null}
          {!!enterpriseGroupBuyId ? (
            <StoreInfoBar
              name={storeInfo.name}
              logo={storeInfo.logo}
              onNavigate={
                !!enterpriseGroupBuyId
                  ? undefined
                  : () =>
                      navigate(
                        getPath(`/store/${storeInfo.id}/info`, groupId, enterpriseGroupBuyId),
                        {
                          state: { previous: window.location.pathname },
                        },
                      )
              }
            />
          ) : null}
          {!enterpriseGroupBuyId && !isPlatformContainStore && (
            <Box px={4} bgColor="danger.200">
              <Content py={2}>
                <Text color="danger.600" fontSize="xs" fontWeight="bold">
                  此餐廳沒有與現在的取餐點合作
                </Text>
              </Content>
            </Box>
          )}
        </>
      }
    >
      <Box bgColor="white" py={{ base: 0, md: 4 }}>
        <Content minH="100vh">
          <Stack align="stretch" direction={{ base: 'column', md: 'row' }} spacing={4}>
            <Box flex="1">
              {itemWithModifiers.image && (
                <AspectRatio ratio={1} pos="sticky" top="150px" m={5}>
                  <Image src={itemWithModifiers.image} fallbackSrc={errorImg} />
                </AspectRatio>
              )}
            </Box>

            <Box flex="1" px={{ base: 4, md: 0 }}>
              <VStack align="stretch" spacing={5}>
                <ItemInfoSection
                  name={itemWithModifiers.name}
                  tags={itemWithModifiers.tags[PLATFORM_ID] || []}
                  description={itemWithModifiers.description}
                  price={itemWithModifiers.price}
                  inventory={
                    fp.eq(restOfItemInventory, Infinity) ? '100+' : restOfItemInventory.toString()
                  }
                />
                {itemWithModifiers.modifiers.map((modifier) =>
                  fp.lte(modifier.max, 1) ? (
                    <SingleModifierGroup
                      key={modifier.id}
                      modifiersErrors={showModifierErrors}
                      modifier={modifier}
                      itemCount={count}
                      cartModifiersMap={cartModifiersMap}
                      positionInfo={positionInfo}
                      errorModifierId={errorModifierId}
                      onChangeModifier={handleChangeCartModifiers}
                    />
                  ) : (
                    <MultiModifierGroup
                      key={modifier.id}
                      modifiersErrors={showModifierErrors}
                      itemCount={count}
                      modifier={modifier}
                      cartModifiersMap={cartModifiersMap}
                      positionInfo={positionInfo}
                      errorModifierId={errorModifierId}
                      onChangeModifier={handleChangeCartModifiers}
                    />
                  ),
                )}
                <NoteInputSection note={note} onChangeNote={handleChangeNote} />
              </VStack>
            </Box>
          </Stack>
        </Content>
      </Box>

      <Box pos="sticky" bottom="0" zIndex={70}>
        <Content>
          {!!enterpriseGroupBuyId ? (
            <>
              {data?.orderEndAt ? (
                <StoreLastPreOrderTimeNotice
                  isEnterpriseGroupBuy
                  isValidTime
                  time={data?.orderEndAt.toDate()}
                />
              ) : null}
            </>
          ) : isInStoreOperatingTime ? (
            <StoreLastPreOrderTimeNotice
              isValidTime={isValidPreOrderTime}
              time={lastPreOrderTime}
            />
          ) : (
            <NotOperatingNotice />
          )}
        </Content>

        {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
          <FooterLoginSection />
        ) : (
          <ItemControlBar
            count={count}
            total={itemTotal}
            isDisabled={isDisabled}
            maxCount={restOfItemInventory}
            onConform={handleAddToCartClick}
            onChangeCount={handleChangeCount}
          />
        )}
      </Box>

      <InventoryErrorModal
        isOpen={isOpen}
        onClose={onClose}
        title="抱歉，細項數量不足"
        content="請重新選擇您的細項"
      />
    </PageContainer>
  );
}
